import {
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CacheDB, DataTable } from '@shared/services/indexed-db';
import { LogsData } from '@shared/services/logs';
import { mergeMap, Observable, of, tap } from 'rxjs';

/**
 * Configuration options for HTTP request caching.
 */
export interface CacheInput {
  /** Cache duration in seconds. */
  cacheExpirationTime: number;

  /** Custom cache key to override the default URL-based key. */
  forcedKey?: string;

  /** Forces cache refresh even if valid cached data exists. */
  refresh?: boolean;

  /** Target IndexedDB store for caching. */
  store?: keyof typeof DataTable;
}

/**
 * Context token controlling cache activation for a request.
 *
 * @example
 * ```
 * const context = new HttpContext().set(IS_CACHE_ENABLED, true);
 * this.http.get('/api/data', { context });
 * ```
 */
export const IS_CACHE_ENABLED = new HttpContextToken<boolean>(() => false);

/**
 * Context token providing cache configuration for a request.
 *
 * @example
 * ```
 * const context = new HttpContext()
 *   .set(IS_CACHE_ENABLED, true)
 *   .set(CACHE, { cacheExpirationTime: 600, store: 'CACHE' });
 * this.http.get('/api/data', { context });
 * ```
 */
export const CACHE = new HttpContextToken<CacheInput>(() => ({
  cacheExpirationTime: 0,
  refresh: false,
  store: 'CACHE',
}));

/**
 * HTTP interceptor implementing automatic response caching with IndexedDB.
 *
 * @remarks
 * - Only caches GET requests when `IS_CACHE_ENABLED` is `true`
 * - Uses request URL with params as default cache key
 * - Cached responses are returned immediately without server call
 *
 * @example
 * ```
 * // In application config
 * provideHttpClient(withInterceptors([CacheInterceptor]));
 *
 * // In a service
 * export class DataService {
 *   private http = inject(HttpClient);
 *
 *   getData(): Observable<Data> {
 *     const context = new HttpContext()
 *       .set(IS_CACHE_ENABLED, true)
 *       .set(CACHE, { cacheExpirationTime: 300 });
 *
 *     return this.http.get<Data>('/api/data', { context });
 *   }
 * }
 * ```
 */
@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private readonly cacheDB = inject(CacheDB);
  private readonly logs = inject(LogsData);

  /**
   * Intercepts HTTP requests to provide caching functionality.
   *
   * @param req - Outgoing HTTP request
   * @param next - Next handler in the interceptor chain
   * @returns Observable of HTTP event (cached or fresh response)
   *
   * @remarks
   * - Non-GET requests bypass caching
   * - Fresh responses are automatically cached for subsequent requests
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.method !== 'GET' || !req.context.get(IS_CACHE_ENABLED)) {
      return next.handle(req);
    }

    const { cacheExpirationTime, forcedKey, refresh, store } = req.context.get(CACHE);
    const key = forcedKey ?? req.urlWithParams;

    return this.cacheDB.get<unknown>(key, store ?? 'CACHE').pipe(
      mergeMap((value: unknown) => {
        if (value && !refresh) {
          this.logs.debug(`%c[CACHE] ${req.method} ${req.url}`, 'color: #868e96');
          return of(new HttpResponse<unknown>({ body: value }));
        }

        return next.handle(req).pipe(
          tap((valueLoaded) => {
            if (valueLoaded instanceof HttpResponse) {
              this.cacheDB.upsert(key, valueLoaded.body, cacheExpirationTime, store ?? 'CACHE').subscribe();
            }
          })
        );
      })
    );
  }
}
